import { useEffect, useState, useCallback } from 'react';
import { Building2, Plus, X, ChevronRight, Users, Trash2, Mail, UserPlus, Check, AlertTriangle, Moon, Sun, Waves, Leaf, Sunset, Sparkles, Mountain } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import ColorSelector from '../components/ColorSelector.js';
import Layout from '../components/Layout.js';
import { useAuth } from '../contexts/AuthContext.js';
import { Navigate } from 'react-router-dom';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { TierOverrideForm, isOverrideActive, formatOverride } from '../components/TierOverrideForm.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OrgListItem {
  id: string;
  name: string;
  slug: string;
  subscription_tier: string;
  tier_override?: string | null;
  tier_override_until?: string | null;
  tier_override_note?: string | null;
  user_limit: number;
  user_count: number;
  owner_count: number;
  created_at: string;
}

interface OrgSettings {
  theme_variant?: string;
  default_card_color?: string;
  allow_theme_customisation?: boolean;
  allow_image_customisation?: boolean;
  allow_links_customisation?: boolean;
  allow_privacy_customisation?: boolean;
  app_theme_overrides?: Record<string, { light?: string; dark?: string }>;
}

interface OrgDetail extends OrgListItem {
  settings: OrgSettings;
  owners: Array<{ id: string; email: string; created_at: string }>;
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const inputCls =
  'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const labelCls = 'block text-xs font-medium text-ink-muted mb-1.5';
const btnPrimary =
  'inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity';
const btnGhost =
  'inline-flex items-center gap-2 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink hover:bg-raised hover:text-ink transition-all';
const btnDanger =
  'inline-flex items-center gap-2 rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-3 py-1.5 text-sm text-danger-700 dark:text-danger-300 hover:bg-danger-500/20 hover:border-danger-500/50 transition-all';

// ─── Brand kit presets ───────────────────────────────────────────────────────

interface BrandKit {
  id: string;
  name: string;
  icon: LucideIcon;
  accent: string;       // hex for preview swatch
  defaultCardColor?: string;
  overrides: Record<string, { light: string; dark: string }>;
}

const BRAND_KITS: BrandKit[] = [
  {
    id: 'midnight',
    name: 'Midnight',
    icon: Moon,
    accent: '#7c3aed',
    defaultCardColor: 'violet',
    overrides: {
      main:        { light: '#f5f4ff', dark: '#0a0618' },
      card:        { light: '#ffffff', dark: '#0f0a2e' },
      surface:     { light: '#ede9fe', dark: '#1a1040' },
      border:      { light: '#ddd6fe', dark: '#2e1e6b' },
      inputBorder: { light: '#c4b5fd', dark: '#3730a3' },
      textPrimary: { light: '#1e1b4b', dark: '#f5f3ff' },
      textSecondary:{ light: '#4c1d95', dark: '#c4b5fd' },
      textMuted:   { light: '#6d28d9', dark: '#a78bfa' },
    },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    icon: Waves,
    accent: '#0891b2',
    defaultCardColor: 'cyan',
    overrides: {
      main:        { light: '#f0fbff', dark: '#020f18' },
      card:        { light: '#ffffff', dark: '#051828' },
      surface:     { light: '#e0f7fa', dark: '#0c2a3c' },
      border:      { light: '#b2ebf2', dark: '#1a4a5e' },
      inputBorder: { light: '#80deea', dark: '#0e7490' },
      textPrimary: { light: '#012e3d', dark: '#e0f7fa' },
      textSecondary:{ light: '#065f46', dark: '#67e8f9' },
      textMuted:   { light: '#0891b2', dark: '#22d3ee' },
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    icon: Leaf,
    accent: '#047857',
    defaultCardColor: 'emerald',
    overrides: {
      main:        { light: '#f0fff5', dark: '#020d08' },
      card:        { light: '#ffffff', dark: '#051a0f' },
      surface:     { light: '#dcfce7', dark: '#0c2e18' },
      border:      { light: '#bbf7d0', dark: '#14532d' },
      inputBorder: { light: '#86efac', dark: '#166534' },
      textPrimary: { light: '#052e16', dark: '#dcfce7' },
      textSecondary:{ light: '#14532d', dark: '#6ee7b7' },
      textMuted:   { light: '#047857', dark: '#34d399' },
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    icon: Sunset,
    accent: '#e11d48',
    defaultCardColor: 'rose',
    overrides: {
      main:        { light: '#fff8f6', dark: '#160508' },
      card:        { light: '#ffffff', dark: '#250a0e' },
      surface:     { light: '#ffe4e6', dark: '#3d1018' },
      border:      { light: '#fecdd3', dark: '#6b1423' },
      inputBorder: { light: '#fda4af', dark: '#9f1239' },
      textPrimary: { light: '#1f0410', dark: '#ffe4e6' },
      textSecondary:{ light: '#881337', dark: '#fda4af' },
      textMuted:   { light: '#be123c', dark: '#fb7185' },
    },
  },
  {
    id: 'golden',
    name: 'Golden',
    icon: Sparkles,
    accent: '#b45309',
    defaultCardColor: 'amber',
    overrides: {
      main:        { light: '#fffbeb', dark: '#120a01' },
      card:        { light: '#ffffff', dark: '#1f1205' },
      surface:     { light: '#fef3c7', dark: '#362008' },
      border:      { light: '#fde68a', dark: '#5c3a0e' },
      inputBorder: { light: '#fcd34d', dark: '#78350f' },
      textPrimary: { light: '#1c0a00', dark: '#fef3c7' },
      textSecondary:{ light: '#78350f', dark: '#fcd34d' },
      textMuted:   { light: '#b45309', dark: '#fbbf24' },
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    icon: Mountain,
    accent: '#334155',
    defaultCardColor: 'slate',
    overrides: {
      main:        { light: '#f8fafc', dark: '#0a0e14' },
      card:        { light: '#ffffff', dark: '#111827' },
      surface:     { light: '#f1f5f9', dark: '#1f2937' },
      border:      { light: '#e2e8f0', dark: '#374151' },
      inputBorder: { light: '#cbd5e1', dark: '#4b5563' },
      textPrimary: { light: '#0f172a', dark: '#f1f5f9' },
      textSecondary:{ light: '#1e293b', dark: '#94a3b8' },
      textMuted:   { light: '#475569', dark: '#64748b' },
    },
  },
];

const COLOR_TOKENS = [
  {
    group: 'Hintergrund',
    tokens: [
      { key: 'main',    label: 'Seitenhintergrund' },
      { key: 'card',    label: 'Karte' },
      { key: 'surface', label: 'Oberfläche / Inputs' },
    ],
  },
  {
    group: 'Buttons & Links',
    tokens: [
      { key: 'action',  label: 'Primär-Button' },
      { key: 'confirm', label: 'Grüner CTA' },
      { key: 'link',    label: 'Link-Akzentfarbe' },
    ],
  },
  {
    group: 'Text',
    tokens: [
      { key: 'textPrimary',   label: 'Primärtext' },
      { key: 'textSecondary', label: 'Sekundärtext' },
      { key: 'textMuted',     label: 'Gedämpfter Text' },
    ],
  },
  {
    group: 'Rahmen',
    tokens: [
      { key: 'border',       label: 'Rahmenfarbe' },
      { key: 'inputBorder',  label: 'Input-Rahmen' },
    ],
  },
] as const;

// ─── Toggle component ────────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${
        checked ? 'bg-accent-600' : 'bg-slate-300 dark:bg-white/15'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// ─── Create Org Drawer ───────────────────────────────────────────────────────

function CreateOrgDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [userLimit, setUserLimit] = useState('3');
  const [defaultCardColor, setDefaultCardColor] = useState('');
  const [ownerMode, setOwnerMode] = useState<'none' | 'invite' | 'create'>('none');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name,
        userLimit: parseInt(userLimit, 10),
        ...(defaultCardColor ? { defaultCardColor } : {}),
      };
      if (ownerMode !== 'none' && ownerEmail) {
        body.ownerEmail = ownerEmail;
        if (ownerMode === 'invite') {
          body.inviteOwner = true;
        } else {
          body.ownerPassword = ownerPassword;
        }
      }

      const res = await fetch('/api/admin/organisations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setError(data.error ?? t('organisations.errors.createFailed')); return; }
      onCreated();
      onClose();
    } catch {
      setError(t('organisations.errors.network'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DrawerShell title={t('organisations.create.title')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label className={labelCls}>{t('common.name')} *</label>
          <input
            type="text"
            required
            maxLength={200}
            placeholder={t('organisations.create.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
        </div>

        {/* User Limit */}
        <div>
          <label className={labelCls}>{t('organisations.userLimit')}</label>
          <input
            type="number"
            min={1}
            max={100000}
            value={userLimit}
            onChange={(e) => setUserLimit(e.target.value)}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-ink-faint">{t('organisations.create.userLimitHint')}</p>
        </div>

        {/* Default card color */}
        <div>
          <label className={labelCls}>{t('organisations.create.defaultCardColor')}</label>
          <p className="text-xs text-ink-faint mb-3">
            {t('organisations.create.defaultCardColorHint')}
          </p>
          <ColorSelector
            selectedColor={defaultCardColor || 'indigo'}
            onSelect={(c) => setDefaultCardColor(c)}
          />
          {defaultCardColor && (
            <button type="button" onClick={() => setDefaultCardColor('')}
              className="mt-2 text-xs text-ink-faint hover:text-danger-400 transition-colors">
              {t('organisations.create.resetDefaultColor')}
            </button>
          )}
        </div>

        {/* Owner */}
        <div className="rounded-xl bg-panel border border-line p-4 space-y-3">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('organisations.create.addOwner')}</p>
          <div className="flex gap-2">
            {(['none', 'invite', 'create'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setOwnerMode(mode)}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  ownerMode === mode
                    ? 'bg-accent-100 dark:bg-accent-600/30 border border-accent-500/50 text-accent-700 dark:text-accent-300'
                    : 'bg-slate-200 dark:bg-white/5 border border-line text-ink-muted hover:text-ink'
                }`}
              >
                {mode === 'none' ? t('organisations.owner.skip') : mode === 'invite' ? t('organisations.owner.invite') : t('organisations.owner.createDirect')}
              </button>
            ))}
          </div>

          {ownerMode !== 'none' && (
            <div className="space-y-3 pt-1">
              <div>
                <label className={labelCls}>{t('common.email')}</label>
                <input
                  type="email"
                  required
                  placeholder={t('organisations.owner.emailPlaceholder')}
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  className={inputCls}
                />
              </div>
              {ownerMode === 'create' && (
                <div>
                  <label className={labelCls}>{t('organisations.owner.passwordLabel')}</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="••••••••"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    className={inputCls}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-danger-100 dark:bg-danger-500/10 border border-danger-500/30 px-3 py-2 text-sm text-danger-700 dark:text-danger-300">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? t('common.states.creating') : t('organisations.create.submit')}
          </button>
          <button type="button" onClick={onClose} className={btnGhost}>{t('common.cancel')}</button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ─── Edit Org Drawer ──────────────────────────────────────────────────────────

function EditOrgDrawer({
  orgId,
  onClose,
  onUpdated,
}: {
  orgId: string;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { t } = useAppTranslation();
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'general' | 'plan' | 'branding' | 'owner'>('general');

  // General tab state
  const [name, setName] = useState('');
  const [userLimit, setUserLimit] = useState('');
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savedGeneral, setSavedGeneral] = useState(false);

  // Branding tab state
  const [themeVariant, setThemeVariant] = useState('vcard');
  const [defaultCardColor, setDefaultCardColor] = useState('');
  const [allowTheme, setAllowTheme] = useState(true);
  const [allowImage, setAllowImage] = useState(true);
  const [allowLinks, setAllowLinks] = useState(true);
  const [allowPrivacy, setAllowPrivacy] = useState(true);
  const [colorOverrides, setColorOverrides] = useState<Record<string, { light: string; dark: string }>>({});
  const [savingBranding, setSavingBranding] = useState(false);
  const [savedBranding, setSavedBranding] = useState(false);

  // Owner tab state
  const [ownerMode, setOwnerMode] = useState<'invite' | 'create'>('invite');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);
  const [ownerResult, setOwnerResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Delete state
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [error, setError] = useState('');

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/organisations/${orgId}`, { credentials: 'include' });
      if (!res.ok) { setError(t('organisations.errors.notFound')); return; }
      const data = await res.json() as OrgDetail;
      setDetail(data);
      setName(data.name);
      setUserLimit(String(data.user_limit));
      setThemeVariant(data.settings.theme_variant ?? 'vcard');
      setDefaultCardColor(data.settings.default_card_color ?? '');
      setAllowTheme(data.settings.allow_theme_customisation ?? true);
      setAllowImage(data.settings.allow_image_customisation ?? true);
      setAllowLinks(data.settings.allow_links_customisation ?? true);
      setAllowPrivacy(data.settings.allow_privacy_customisation ?? true);
      const overrides = data.settings.app_theme_overrides ?? {};
      const loaded: Record<string, { light: string; dark: string }> = {};
      for (const token of COLOR_TOKENS.map(g => g.tokens).flat().map(t => t.key)) {
        loaded[token] = {
          light: (overrides[token]?.light ?? '') as string,
          dark: (overrides[token]?.dark ?? '') as string,
        };
      }
      setColorOverrides(loaded);
    } catch {
      setError(t('organisations.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { void loadDetail(); }, [loadDetail]);

  async function saveGeneral(e: React.FormEvent) {
    e.preventDefault();
    setSavingGeneral(true);
    setSavedGeneral(false);
    try {
      const res = await fetch(`/api/admin/organisations/${orgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, userLimit: parseInt(userLimit, 10) }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setError(data.error ?? t('common.error')); return; }
      setSavedGeneral(true);
      onUpdated();
      void loadDetail();
    } finally {
      setSavingGeneral(false);
    }
  }

  async function saveBranding(e: React.FormEvent) {
    e.preventDefault();
    setSavingBranding(true);
    setSavedBranding(false);
    try {
      await fetch(`/api/admin/organisations/${orgId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          theme_variant: themeVariant,
          ...(defaultCardColor ? { default_card_color: defaultCardColor } : {}),
          allow_theme_customisation: allowTheme,
          allow_image_customisation: allowImage,
          allow_links_customisation: allowLinks,
          allow_privacy_customisation: allowPrivacy,
          app_theme_overrides: Object.fromEntries(
            Object.entries(colorOverrides)
              .filter(([, v]) => v.light || v.dark)
              .map(([k, v]) => [k, { ...(v.light ? { light: v.light } : {}), ...(v.dark ? { dark: v.dark } : {}) }])
          ),
        }),
      });
      setSavedBranding(true);
      onUpdated();
    } finally {
      setSavingBranding(false);
    }
  }

  async function addOwner(e: React.FormEvent) {
    e.preventDefault();
    setSavingOwner(true);
    setOwnerResult(null);
    try {
      const endpoint = ownerMode === 'invite'
        ? `/api/admin/organisations/${orgId}/invite-owner`
        : `/api/admin/organisations/${orgId}/create-owner`;
      const body = ownerMode === 'invite'
        ? { email: ownerEmail }
        : { email: ownerEmail, password: ownerPassword };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) {
        setOwnerResult({ type: 'error', message: data.error ?? t('common.error') });
      } else {
        setOwnerResult({
          type: 'success',
          message: ownerMode === 'invite' ? t('organisations.owner.inviteSent') : t('organisations.owner.created'),
        });
        setOwnerEmail('');
        setOwnerPassword('');
        void loadDetail();
      }
    } finally {
      setSavingOwner(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`/api/admin/organisations/${orgId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation: deleteConfirm }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setDeleteError(data.error ?? t('organisations.errors.deleteFailed')); return; }
      onUpdated();
      onClose();
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <DrawerShell title={t('organisations.loadingTitle')} onClose={onClose}>
        <div className="flex items-center justify-center py-16 text-ink-faint">{t('common.loading')}</div>
      </DrawerShell>
    );
  }

  if (error || !detail) {
    return (
      <DrawerShell title={t('common.error')} onClose={onClose}>
        <p className="text-danger-700 dark:text-danger-300 text-sm">{error || t('organisations.errors.unknown')}</p>
      </DrawerShell>
    );
  }

  const tabs = [
    { id: 'general', label: t('organisations.tabs.general') },
    { id: 'plan', label: t('organisations.tabs.plan', 'Plan') },
    { id: 'branding', label: t('organisations.tabs.branding') },
    { id: 'owner', label: t('organisations.tabs.owner', '', { count: detail.owners.length }) },
  ] as const;

  return (
    <DrawerShell title={detail.name} onClose={onClose}>
      {/* Tabs */}
      <div className="flex gap-1 rounded-xl bg-panel p-1 mb-5">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-accent-100 dark:bg-accent-600/30 text-accent-700 dark:text-accent-300'
                : 'text-ink-muted hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Plan Tab: unlock a plan without payment ── */}
      {activeTab === 'plan' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-panel p-4 text-sm">
            <p className="text-xs text-ink-muted uppercase tracking-wide">{t('organisations.plan.purchased', 'Gebuchter Plan')}</p>
            <p className="mt-1 font-semibold text-ink capitalize">{detail.subscription_tier}</p>
          </div>
          <TierOverrideForm
            kind="organisation"
            id={detail.id}
            initial={{ tier: detail.tier_override ?? null, until: detail.tier_override_until ?? null, note: detail.tier_override_note ?? null }}
            onSaved={() => { onUpdated(); void loadDetail(); }}
          />
        </div>
      )}

      {/* ── General Tab ── */}
      {activeTab === 'general' && (
        <div className="space-y-5">
          <form onSubmit={saveGeneral} className="space-y-4">
            <div>
              <label className={labelCls}>{t('common.name')}</label>
              <input
                type="text"
                required
                maxLength={200}
                value={name}
                onChange={(e) => { setName(e.target.value); setSavedGeneral(false); }}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-ink-faint">{t('organisations.slug')}: {detail.slug}</p>
            </div>
            <div>
              <label className={labelCls}>{t('organisations.userLimit')}</label>
              <input
                type="number"
                min={1}
                max={100000}
                value={userLimit}
                onChange={(e) => { setUserLimit(e.target.value); setSavedGeneral(false); }}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-ink-faint">
                {t('organisations.activeUsers', '', { count: detail.user_count, limit: detail.user_limit })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={savingGeneral} className={btnPrimary}>
                {savingGeneral ? t('settings.saving') : t('common.save')}
              </button>
              {savedGeneral && (
                <span className="flex items-center gap-1 text-sm text-success-400">
                  <Check size={14} /> {t('settings.saved')}
                </span>
              )}
            </div>
          </form>

          {/* Delete section */}
          <div className="border-t border-line pt-5">
            {!showDelete ? (
              <button type="button" onClick={() => setShowDelete(true)} className={btnDanger}>
                <Trash2 size={14} />
                {t('organisations.delete.open')}
              </button>
            ) : (
              <div className="space-y-3 rounded-xl bg-danger-500/5 border border-danger-500/20 p-4">
                <p className="text-sm text-danger-700 dark:text-danger-300 font-medium flex items-center gap-2">
                  <AlertTriangle size={14} />
                  {t('organisations.delete.warning')}
                </p>
                <p className="text-xs text-ink-muted">
                  {t('organisations.delete.confirmText', '', { slug: detail.slug })}
                </p>
                <input
                  type="text"
                  placeholder={`DELETE ${detail.slug}`}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className={inputCls}
                />
                {deleteError && <p className="text-xs text-danger-400">{deleteError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={deleting || deleteConfirm !== `DELETE ${detail.slug}`}
                    onClick={handleDelete}
                    className={btnDanger}
                  >
                    {deleting ? t('common.states.deleting') : t('organisations.delete.confirm')}
                  </button>
                  <button type="button" onClick={() => { setShowDelete(false); setDeleteConfirm(''); }} className={btnGhost}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Branding Tab ── */}
      {activeTab === 'branding' && (
        <form onSubmit={saveBranding} className="space-y-5">
          {/* Theme Variant */}
          <div>
            <label className={labelCls}>{t('organisations.branding.theme')}</label>
            <div className="flex gap-2">
              {['vcard', 'minimal', 'mint'].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setThemeVariant(v)}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium capitalize transition-all ${
                    themeVariant === v
                      ? 'bg-accent-100 dark:bg-accent-600/30 border border-accent-500/50 text-accent-700 dark:text-accent-300'
                      : 'bg-panel border border-line text-ink-muted hover:text-ink'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Default card color */}
          <div>
            <label className={labelCls}>{t('organisations.create.defaultCardColor')}</label>
            <p className="text-xs text-ink-faint mb-3">
              {t('organisations.branding.defaultColorHint')}
            </p>
            <ColorSelector
              selectedColor={defaultCardColor || 'indigo'}
              onSelect={(c) => setDefaultCardColor(c)}
            />
            {defaultCardColor && (
              <button
                type="button"
                onClick={() => setDefaultCardColor('')}
                className="mt-2 text-xs text-ink-faint hover:text-danger-400 transition-colors"
              >
                {t('organisations.branding.resetDefault')}
              </button>
            )}
          </div>

          {/* Feature flags */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
              {t('organisations.branding.permissions')}
            </p>
            {[
              { key: 'theme', label: t('settings.featureFlags.theme'), value: allowTheme, set: setAllowTheme },
              { key: 'image', label: t('settings.featureFlags.images'), value: allowImage, set: setAllowImage },
              { key: 'links', label: t('settings.featureFlags.links'), value: allowLinks, set: setAllowLinks },
              { key: 'privacy', label: t('settings.featureFlags.privacy'), value: allowPrivacy, set: setAllowPrivacy },
            ].map(({ key, label, value, set }) => (
              <label key={key} className="flex items-center justify-between cursor-pointer group">
                <span className="text-sm text-ink-muted group-hover:text-slate-700 dark:group-hover:text-white/80 transition-colors">{label}</span>
                <Toggle checked={value} onChange={set} />
              </label>
            ))}
          </div>

          {/* Brand Kits */}
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Brand Kits</p>
              <p className="text-xs text-ink-faint mt-1">Ein Klick setzt alle Kartenfarben auf ein modernes Preset — danach beliebig anpassen.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {BRAND_KITS.map((kit) => (
                <button
                  key={kit.id}
                  type="button"
                  onClick={() => {
                    // Merge kit overrides into colorOverrides
                    setColorOverrides((prev) => {
                      const next = { ...prev };
                      for (const [k, v] of Object.entries(kit.overrides)) {
                        next[k] = { light: v.light, dark: v.dark };
                      }
                      return next;
                    });
                    if (kit.defaultCardColor) setDefaultCardColor(kit.defaultCardColor);
                  }}
                  className="flex flex-col items-start gap-2 rounded-xl bg-panel border border-line px-3 py-2.5 hover:bg-raised hover:border-line-strong transition-all text-left group"
                >
                  <div className="flex items-center gap-2 w-full">
                    <kit.icon className="h-4 w-4 shrink-0" style={{ color: kit.accent }} />
                    <span className="text-xs font-semibold text-ink group-hover:text-slate-800 dark:group-hover:text-white/90 transition-colors">{kit.name}</span>
                    <div
                      className="ml-auto w-3 h-3 rounded-full flex-shrink-0 ring-1 ring-white/20"
                      style={{ backgroundColor: kit.accent }}
                    />
                  </div>
                  <div className="flex gap-1 w-full">
                    {(['main', 'card', 'surface'] as const).map((token) => (
                      <div
                        key={token}
                        className="flex-1 h-1.5 rounded-full"
                        style={{ backgroundColor: kit.overrides[token]?.dark ?? '#333' }}
                      />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* App Color Overrides */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
              App-Farben (Hell / Dunkel)
            </p>
            <p className="text-xs text-ink-faint">
              Überschreibt die Theme-Farben individuell. Leer lassen = Theme-Standard.
            </p>
            {COLOR_TOKENS.map((group) => (
              <div key={group.group} className="space-y-2">
                <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide">{group.group}</p>
                {group.tokens.map((token) => {
                  const val = colorOverrides[token.key] ?? { light: '', dark: '' };
                  const setVal = (side: 'light' | 'dark', v: string) =>
                    setColorOverrides((prev) => ({
                      ...prev,
                      [token.key]: { ...(prev[token.key] ?? { light: '', dark: '' }), [side]: v },
                    }));
                  return (
                    <div key={token.key} className="flex items-center gap-3">
                      <span className="text-xs text-ink-muted w-36 flex-shrink-0">{token.label}</span>
                      <div className="flex gap-2 flex-1">
                        {(['light', 'dark'] as const).map((side) => (
                          <div key={side} className="flex items-center gap-1.5 flex-1 min-w-0">
                            <div
                              className="w-6 h-6 rounded flex-shrink-0 border border-line-strong cursor-pointer"
                              style={{ backgroundColor: val[side] || (side === 'light' ? '#ffffff' : '#000000') }}
                              onClick={() => {
                                const input = document.getElementById(`color-${token.key}-${side}`);
                                if (input) (input as HTMLInputElement).click();
                              }}
                            />
                            <input
                              id={`color-${token.key}-${side}`}
                              type="color"
                              className="sr-only"
                              value={val[side] || (side === 'light' ? '#ffffff' : '#000000')}
                              onChange={(e) => setVal(side, e.target.value)}
                            />
                            <input
                              type="text"
                              placeholder={side === 'light' ? '#ffffff' : '#000000'}
                              value={val[side]}
                              maxLength={7}
                              onChange={(e) => {
                                const v = e.target.value;
                                setVal(side, v);
                              }}
                              className="block w-full rounded bg-panel border border-line px-2 py-1 text-xs text-ink font-mono placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50"
                            />
                            <span className="text-ink-faint flex-shrink-0 inline-flex">
                              {side === 'light' ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
                            </span>
                          </div>
                        ))}
                        {(val.light || val.dark) && (
                          <button
                            type="button"
                            onClick={() => setColorOverrides((prev) => ({ ...prev, [token.key]: { light: '', dark: '' } }))}
                            className="text-ink-faint hover:text-danger-400 transition-colors flex-shrink-0 p-0.5"
                            title="Zurücksetzen"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={savingBranding} className={btnPrimary}>
              {savingBranding ? t('settings.saving') : t('organisations.branding.save')}
            </button>
            {savedBranding && (
              <span className="flex items-center gap-1 text-sm text-success-400">
                <Check size={14} /> {t('settings.saved')}
              </span>
            )}
          </div>
        </form>
      )}

      {/* ── Owner Tab ── */}
      {activeTab === 'owner' && (
        <div className="space-y-5">
          {/* Existing owners */}
          {detail.owners.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('organisations.owner.current')}</p>
              {detail.owners.map((owner) => (
                <div key={owner.id} className="flex items-center gap-3 rounded-lg bg-panel border border-line px-3 py-2.5">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-600/30 text-xs font-bold text-accent-700 dark:text-accent-300">
                    {owner.email.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-sm text-ink truncate">{owner.email}</p>
                </div>
              ))}
            </div>
          )}

          {/* Add owner */}
          <div className="rounded-xl bg-panel border border-line p-4 space-y-4">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('organisations.create.addOwner')}</p>

            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOwnerMode('invite')}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  ownerMode === 'invite'
                    ? 'bg-accent-100 dark:bg-accent-600/30 border border-accent-500/50 text-accent-700 dark:text-accent-300'
                    : 'bg-slate-200 dark:bg-white/5 border border-line text-ink-muted hover:text-ink'
                }`}
              >
                <Mail size={12} /> {t('organisations.owner.invite')}
              </button>
              <button
                type="button"
                onClick={() => setOwnerMode('create')}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  ownerMode === 'create'
                    ? 'bg-accent-100 dark:bg-accent-600/30 border border-accent-500/50 text-accent-700 dark:text-accent-300'
                    : 'bg-slate-200 dark:bg-white/5 border border-line text-ink-muted hover:text-ink'
                }`}
              >
                <UserPlus size={12} /> {t('organisations.owner.createDirect')}
              </button>
            </div>

            <form onSubmit={addOwner} className="space-y-3">
              <div>
                <label className={labelCls}>{t('common.email')}</label>
                <input
                  type="email"
                  required
                  placeholder={t('organisations.owner.emailPlaceholder')}
                  value={ownerEmail}
                  onChange={(e) => { setOwnerEmail(e.target.value); setOwnerResult(null); }}
                  className={inputCls}
                />
              </div>
              {ownerMode === 'create' && (
                <div>
                  <label className={labelCls}>{t('organisations.owner.passwordLabel')}</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="••••••••"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    className={inputCls}
                  />
                </div>
              )}

              {ownerResult && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                  ownerResult.type === 'success'
                    ? 'bg-success-100 dark:bg-success-500/10 border border-success-500/30 text-success-700 dark:text-success-300'
                    : 'bg-danger-100 dark:bg-danger-500/10 border border-danger-500/30 text-danger-700 dark:text-danger-300'
                }`}>
                  {ownerResult.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
                  {ownerResult.message}
                </div>
              )}

              <button type="submit" disabled={savingOwner} className={btnPrimary}>
                {savingOwner
                  ? t('settings.saving')
                  : ownerMode === 'invite'
                    ? t('organisations.owner.sendInvite')
                    : t('organisations.owner.create')}
              </button>
            </form>
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

// ─── Drawer shell ─────────────────────────────────────────────────────────────

function DrawerShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-panel border-l border-line shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-muted hover:bg-raised hover:text-ink transition-all"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {children}
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrganisationsPage() {
  const { user, loading: authLoading } = useAuth();
  const [orgs, setOrgs] = useState<OrgListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editOrgId, setEditOrgId] = useState<string | null>(null);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/organisations', { credentials: 'include' });
      const data = await res.json() as { organisations: OrgListItem[] };
      setOrgs(data.organisations ?? []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOrgs(); }, [loadOrgs]);

  // Guard: superadmin only
  if (!authLoading && user?.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout title="Organisationen">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-ink">Organisationen</h1>
            <p className="text-sm text-ink-muted mt-0.5">
              {orgs.length} {orgs.length === 1 ? 'Organisation' : 'Organisationen'}
            </p>
          </div>
          <button type="button" onClick={() => setShowCreate(true)} className={btnPrimary}>
            <Plus size={15} />
            Neue Organisation
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-ink-faint">Laden…</div>
        ) : orgs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <Building2 size={32} className="text-ink-faint" />
            <p className="text-ink-muted text-sm">Noch keine Organisationen vorhanden</p>
            <button type="button" onClick={() => setShowCreate(true)} className={btnPrimary}>
              <Plus size={14} />
              Erste Organisation erstellen
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-line overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-panel">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide hidden sm:table-cell">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">Benutzer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide hidden md:table-cell">Plan</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {orgs.map((org) => (
                  <tr
                    key={org.id}
                    className="hover:bg-slate-100 dark:hover:bg-white/[0.03] transition-colors cursor-pointer"
                    onClick={() => setEditOrgId(org.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-accent-100 dark:bg-accent-600/20 text-xs font-bold text-accent-700 dark:text-accent-300">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{org.name}</p>
                          {org.owner_count === 0 && (
                            <p className="text-xs text-warning-400/70">Kein Owner</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-muted font-mono text-xs hidden sm:table-cell">{org.slug}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Users size={13} className="text-ink-faint" />
                        <span className="text-ink">
                          {org.user_count}
                          <span className="text-ink-faint"> / {org.user_limit}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="inline-flex items-center rounded-full bg-raised px-2 py-0.5 text-xs text-ink-muted capitalize">
                        {org.subscription_tier}
                      </span>
                      {isOverrideActive({ tier: org.tier_override ?? null, until: org.tier_override_until ?? null, note: null }) && (
                        <span
                          title={org.tier_override_note ?? ''}
                          className="ml-1.5 inline-flex items-center rounded-full bg-accent-100 dark:bg-accent-500/15 px-2 py-0.5 text-xs font-medium text-accent-700 dark:text-accent-300"
                        >
                          {formatOverride({ tier: org.tier_override ?? null, until: org.tier_override_until ?? null, note: null })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={16} className="text-ink-faint ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawers */}
      {showCreate && (
        <CreateOrgDrawer
          onClose={() => setShowCreate(false)}
          onCreated={loadOrgs}
        />
      )}
      {editOrgId && (
        <EditOrgDrawer
          orgId={editOrgId}
          onClose={() => setEditOrgId(null)}
          onUpdated={loadOrgs}
        />
      )}
    </Layout>
  );
}
